import colors from './colors';
import { convertHex } from '../utils';

export const GlobalInserts = `
  *, *::before, *::after {
    box-sizing: border-box;
  }

  html, body {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen",
      "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue",
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    height: 100%;
    width: 100%;
    overflow: hidden;
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, "Courier New",
      monospace;
  }

  /* AER-1465 (Danielle 2026-05-15 follow-up — userHighlightColor): browser
     default text-selection highlight is blue, but every other accent in
     the app uses primary purple. Force ::selection to the brand color so
     highlighted text matches the rest of the UI. */
  ::selection {
    background-color: ${colors.primary};
    color: ${colors.white};
  }
  ::-moz-selection {
    background-color: ${colors.primary};
    color: ${colors.white};
  }

  #root {
    height: 100%;
  }
  
  .App, .ant-app {
    height: 100%;
    min-height: 100%;
    display: flex;
    flex-direction: column;
  }

  /* AER-1465: antd v5/v6 ships a token-driven shadow on every .ant-btn
     variant (primary buttons get a pinkish/purple shadow from primaryShadow,
     default buttons get defaultShadow). Force it off across every variant so
     the dark-purple primary buttons stop showing a light-pink halo. */
  .ant-btn,
  .ant-btn-primary,
  .ant-btn-default,
  .ant-btn-dashed,
  .ant-btn-danger,
  .ant-btn-link,
  .ant-btn-text,
  .ant-btn-variant-solid,
  .ant-btn-variant-outlined,
  .ant-btn-variant-dashed,
  .ant-btn-variant-filled,
  .ant-btn-variant-text,
  .ant-btn-variant-link {
    box-shadow: none !important;
  }

  .ant-form-item-label {
    padding-bottom: 0px;
  }

  .ant-row.ant-form-item,
  .ant-form-item {
    margin-bottom: 5px;
  }

  .ant-form-item-label label {
    font-size: 12px;
    font-weight: bold;
    color: ${colors.labelColor};
  }

  #tg .ant-select-selector {
    min-width: 200px;
  }

  /* AER-1648: AntD6 renamed the Select inner box from .ant-select-selection
     (v3) to .ant-select-selector, so the original squared-corner / grey-border
     rule had become dead CSS and Selects rendered with rounded corners. Target
     the v6 class so every Select (incl. AutoComplete, which renders a Select
     internally) keeps the 2px grey resting border and square corners. The
     active/hover border colour + popup selected-option background are pinned to
     the brand purple by the Select component token in useAppConfig; Input /
     TextArea square corners + grey border already come from the global
     borderRadius: 0 / lineWidth: 2 / colorBorder tokens, so no extra Input CSS
     is needed here. */
  .ant-select .ant-select-selector {
    border: 2px solid ${colors.inputBorder};
    border-radius: 0px;
  }

  .ant-checkbox-inner {
    border-radius: 0px;
  }

  /* AER-1465 (Danielle 2026-05-15 follow-up — sidebar font/icon sizing):
     antd v6's Menu token fontSize: 14 was being silently overridden to 12
     here, leaving the Admin / Profiles & Presets sidebars one size smaller
     than the QA build. Drop the override so the Menu token (14px) wins.
     iconSize is bumped to 16 in useAppConfig so labels and icons match
     the v3 / QA proportions. */
  .ant-menu-item {
    font-size: 14px;
  }

  .ant-menu-item:hover i.anticon {
    transition: fill 0.3s cubic-bezier(0.645, 0.045, 0.355, 1), 
      border-color 0.3s cubic-bezier(0.645, 0.045, 0.355, 1), 
      background 0.3s cubic-bezier(0.645, 0.045, 0.355, 1), 
      padding 0.15s cubic-bezier(0.645, 0.045, 0.355, 1);
  }

  .ant-menu-inline .ant-menu-item.ant-menu-item-selected::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: auto;
    bottom: 0;
    border-right: 5px solid ${colors.primary};
  }

  .ant-table {
    border: none;
  }

  /* AER-1465 (Danielle 2026-05-14 follow-up — GridHover.mp4): antd v5+
     adds an ugly grey background to the entire column that's currently
     sorted (.ant-table-column-sort). The primary fix is the Table token
     override in useAppConfig.tsx (bodySortBg / headerSortActiveBg /
     headerSortHoverBg = transparent), but we keep these CSS overrides as
     a belt-and-suspenders defense in case the cssinjs cell rule wins
     specificity (it uses the .ant-table-wrapper ancestor). Use
     'transparent' explicitly so the alternating row stripe / row hover
     can show through, instead of 'inherit' which can pull the missing
     <tr> background and leave the column white over a striped row. */
  .ant-table-wrapper .ant-table-tbody > tr > td.ant-table-column-sort,
  .ant-table-wrapper .ant-table-thead > tr > th.ant-table-column-sort,
  .ant-table-wrapper .ant-table-thead > tr > th.ant-table-column-has-sorters:hover {
    background: transparent !important;
  }

  /* AER-1465 (Danielle 2026-05-14 follow-up — GridHover.mp4): row hover
     should be a uniform light purple wash across every cell, matching the
     v3 / QA look. Two issues to defeat here:

     1. We use rgba(...) tokens elsewhere for the hover, but striped rows
        carry .tbl-list-row-0 { background-color: #eaf4f6 } below. A
        translucent purple over a cyan-tinted stripe ends up a different
        shade than the same purple over a white row, so the hovered row
        ended up with two visibly different bands. Use a SOLID equivalent
        of rgba(111, 75, 158, 0.18) on white (~#e5dfee) so striped vs
        unstriped rows render identically when hovered.

     2. The column-sort transparent override above also has td-level
        specificity, so we explicitly include the column-sort + row-hover
        combination here to make sure sorted columns aren't left grey
        while the rest of the row goes purple. */
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-row:hover > td,
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-row:hover > td.ant-table-column-sort,
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-row > td.ant-table-cell-row-hover,
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-row > td.ant-table-cell-row-hover.ant-table-column-sort {
    background: #e5dfee !important;
  }

  /* AER-1465: antd v6 dropped the default 8px right-margin that v3's
     .ant-tag carried. Without it, multiple tags rendered in a row (e.g. the
     Services pills on View Service Record, the user/group tags, and the
     input-tag chips) sit flush against each other. Restore horizontal +
     vertical spacing globally so every Tag instance gets breathing room.
     Also bump padding/font-size back toward the v3 look so the chips don't
     feel anaemic next to surrounding text. !important is needed because
     antd v6 uses CSS-in-JS with a higher selector specificity. */
  .ant-tag {
    margin-right: 8px !important;
    margin-bottom: 4px !important;
    padding: 2px 8px !important;
    font-size: 12px !important;
    line-height: 18px !important;
    border-radius: 4px !important;
  }

  /* AER-1465 (Danielle 2026-05-14 follow-up — viewServiceRecord tags): antd v6
     renders the Tag close icon using token.colorIcon (a mid-gray) instead of
     inheriting the chip's text color. On our dark solid chips (background
     #4d4d4d via color={colors.tagDark}) that grey × is essentially invisible,
     so the QA chips look correct (text + visible ×) while dev chips look like
     blank dark pills. Force the close icon to inherit the tag's white text
     color on solid-variant tags so the × matches v3's appearance.

     v6 also computes a tighter marginInlineStart + larger tagIconSize for the
     close icon, so the × ends up cramped against the label and visually heavy
     compared to v3 (margin-left: 3px, font-size: 10px). Override those on
     every Tag so the chip spacing matches the v3/QA look. */
  .ant-tag .ant-tag-close-icon {
    margin-inline-start: 6px !important;
    font-size: 10px !important;
  }
  .ant-tag.ant-tag-solid .ant-tag-close-icon,
  .ant-tag.ant-tag-solid .ant-tag-close-icon:hover {
    color: #fff !important;
  }

  /* AER-1465: antd v6 DatePicker calendar cell styling fixes.
     1. The token-driven box-shadow halo (the same pinkish/light-purple glow
        that bled onto buttons via primaryShadow) also lights up on every
        .ant-picker-cell-inner. Force it off so the calendar days are flat.
     2. Restore visually-distinct range-start / in-range / range-end states:
        - in-range cells (the ::before slab between the endpoints) should be
          a faint translucent purple, NOT the same solid purple as the ends
        - range-start / range-end / selected single days stay solid primary
          purple with white text, matching the v3/old UI screenshots
        - the range-hover preview slab also gets the lighter translucent fill
          so it doesn't read as "everything selected". */
  .ant-picker-cell,
  .ant-picker-cell-inner {
    box-shadow: none !important;
  }
  .ant-picker-cell .ant-picker-cell-inner {
    background: transparent;
  }
  /* In-range middle cells: background slab via ::before. Lighten to ~12%
     primary so it reads as "between" not "selected". */
  .ant-picker-cell-in-view.ant-picker-cell-in-range::before,
  .ant-picker-cell-in-view.ant-picker-cell-range-start::before,
  .ant-picker-cell-in-view.ant-picker-cell-range-end::before,
  .ant-picker-cell-in-view.ant-picker-cell-range-hover::before,
  .ant-picker-cell-in-view.ant-picker-cell-range-hover-start::before,
  .ant-picker-cell-in-view.ant-picker-cell-range-hover-end::before {
    background: ${convertHex(colors.primary, 0.12)} !important;
  }
  /* Selected cell + range endpoints: solid primary purple, white text. */
  .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner,
  .ant-picker-cell-in-view.ant-picker-cell-range-start .ant-picker-cell-inner,
  .ant-picker-cell-in-view.ant-picker-cell-range-end .ant-picker-cell-inner {
    background: ${colors.primary} !important;
    color: ${colors.white} !important;
  }
  /* Plain hover (non-selected, non-range) - subtle translucent fill. */
  .ant-picker-cell-in-view:not(.ant-picker-cell-selected):not(.ant-picker-cell-range-start):not(.ant-picker-cell-range-end):not(.ant-picker-cell-disabled):hover .ant-picker-cell-inner {
    background: ${convertHex(colors.primary, 0.18)} !important;
  }
  /* Today indicator (border ring) keeps the primary color. */
  .ant-picker-cell-in-view.ant-picker-cell-today .ant-picker-cell-inner::before {
    border-color: ${colors.primary} !important;
  }

  .ant-table .tbl-list-row-0 {
    background-color: ${colors.table0};
  }

  .ant-table .ant-table-thead > tr > th {
    background-color: ${colors.white};
  }

  .ant-table .ant-table-tbody > tr > td {
    border-bottom: none;
  }

  .any-table .ant-table-column-title {
    color: ${colors.labelColor};
  }

  .ant-table-row.canClick {
    cursor: pointer;
  }

  .ant-input-group-addon button {
    border-radius: 0px;
  }

  .ant-input[disabled],
  .ant-select-disabled {
    background-color: ${colors.disabledLight};
    color: ${colors.black};
  }

  .ant-modal-content {
    border-radius: 0px;
  }

  button.ant-btn {
    border-radius: 0px;
  }

  .rbc-day-slot .rbc-event,
  .rbc-event {
    background-color: ${colors.primary};
    border: 1px solid ${colors.primary};
    border-radius: 0px;
  }

  .rbc-event.rbc-selected {
    background-color: ${colors.primary};
  }

  .rbc-current-time-indicator {
    background-color: ${colors.primary};
  }

  .rbc-today {
    background-color: ${convertHex(colors.primary, 0.1)};
  }

  .ant-card {
    border: 2px solid ${colors.inputBorder};
    border-radius: 0px;
  }

  .ant-card .ant-card-body {
    padding: 10px;
  }

  .ant-btn-danger {
    color: #fff;
    background-color: #ff4d4f;
    border-color: #ff4d4f;
  }
  
  .ant-checkbox-disabled.ant-checkbox-checked .ant-checkbox-inner::after {
    border-color: rgba(0, 0, 0, 0.25) !important;
    -webkit-animation-name: none;
    animation-name: none;
  }

    .col-ellipsis {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    .col-ellipsis {
      display: block;
    }
`;

export default {
  logoWrap: `
    margin-bottom: 30px;
    height: 80px;
  `,
  row: `
    display: flex;
    flex-direction: row;
    align-items: center;
  `,
  column: `
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
  pbRow: `
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
  `,
  formContainer: `
    max-width: 400px;
    height: 100%;
  `,
  formAlert: `
    margin-bottom: 10px;
  `,
  hr: `
    width: 100%;
    height: 2px;
    background-color: ${colors.hrBorder};
  `,
  ellipsisText: `
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
    overflow: hidden;
  `,
  error: `
    font-style: italic;
    color: ${colors.error};
  `,
  pbFilterContainer: `
    flex-direction: column;
    align-items: start;
  `,
  pbFilterStyle: `
    width: 100%;
    margin: 5px 0px;
  `,
  hidden: `
    display: none
  `,
  pbButtonContainer: `
    display: flex;
    flex-direction:
    row; width: 100%;
    justify-content: space-between;
    align-items: center;
    margin-top: 15px;
  `,
  fullHeightCenterColumn: `
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
  `,
  header: `
    color: ${colors.primary};
    font-size: 15px;
    font-weight: bold;
  `
};
